// Un mismo producto en varias tiendas (ADR-2026-10-30-un-cambio-en-varias-tiendas). Puro.
//
//   · `tiendasDelProducto`: en cuáles se sincroniza, con los tildes de la ficha o, sin elegir, donde ya está.
//   · `cambioParaMl`: lo que se le escribe a una publicación de Mercado Libre que ya existe, a partir de lo
//     que cambió en la ficha. Mercado Libre, desde acá, cambia precio, descripción, stock y activa/pausada;
//     el resto se dice como "no se aplica".

import type { CambioDeCampo } from "./cambiosContraLaTienda";
import type { ProductoDeLanzamiento } from "./lanzamientosApi";

export type TiendaDeSincronizacion = "tiendanube" | "mercadolibre";

export interface TiendasDelProducto {
  tiendanube: boolean;
  mercadolibre: boolean;
}

/**
 * En qué tiendas se sincroniza. `elegidas` = lo tildado en la ficha (null = no se eligió: las tiendas donde
 * ya está). Una tienda donde no está sólo va si se la tildó a propósito (se crea allá).
 */
export function tiendasDelProducto(
  elegidas: readonly string[] | null | undefined,
  estaEn: TiendasDelProducto,
): TiendasDelProducto {
  if (elegidas == null) return { tiendanube: estaEn.tiendanube, mercadolibre: estaEn.mercadolibre };
  return { tiendanube: elegidas.includes("tiendanube"), mercadolibre: elegidas.includes("mercadolibre") };
}

/** Lo que va a `cambiar-datos` de una publicación de Mercado Libre. */
export interface CambioParaMl {
  precio?: number;
  descripcion?: string;
  stock?: { general?: number; porValores?: Array<{ valores: string[]; stock: number }> };
  estado?: "active" | "paused";
}

const numero = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/**
 * El cambio para Mercado Libre: sólo lo que cambió en la ficha (`cambios`, la misma comparación que manda a
 * Tiendanube) y que Mercado Libre deja escribir desde acá. `noSeAplican` nombra lo demás que cambió.
 */
export function cambioParaMl(
  p: Pick<ProductoDeLanzamiento, "precio" | "descripcion" | "stock" | "variantes" | "visibilidad"> &
    Partial<Pick<ProductoDeLanzamiento, "precioPorVariante">>,
  cambios: readonly CambioDeCampo[],
): { cambio: CambioParaMl | null; noSeAplican: string[] } {
  const cambio: CambioParaMl = {};
  const noSeAplican = new Set<string>();
  const cambia = (re: RegExp) => cambios.some((c) => re.test(c.campo));
  for (const c of cambios) {
    const campo = c.campo.replace(/^variante:[^:]*:/, "");
    if (!["precio", "descripcion", "stock", "visibilidad", "fotos"].includes(campo)) noSeAplican.add(c.etiqueta.replace(/ de .*$/, ""));
  }
  if (cambia(/(^|:)precio$/)) {
    // El precio de la publicación: el de la ficha o, con precio por variante, el menor de las variantes
    // (Mercado Libre tiene un precio por publicación desde acá). Con precio por variante el general sigue
    // guardado pero no vale: sólo cuenta para las variantes que no tienen el suyo (review Greptile de #232).
    const general = numero(p.precio);
    const precios = (
      p.precioPorVariante && p.variantes.length > 0 ? p.variantes.map((v) => numero(v.precio) ?? general) : [general]
    ).filter((n): n is number => n !== null && n > 0);
    if (precios.length > 0) cambio.precio = Math.min(...precios);
  }
  // Vaciarla también es un cambio: Mercado Libre la deja vacía (review Greptile de #232).
  if (cambia(/^descripcion$/)) cambio.descripcion = (p.descripcion ?? "").trim();
  if (cambia(/(^|:)stock$/)) {
    const porValores = p.variantes.flatMap((v) => {
      const n = numero(v.stock);
      return n === null ? [] : [{ valores: v.valores, stock: Math.max(0, Math.floor(n)) }];
    });
    const general = numero(p.stock);
    if (porValores.length > 0) cambio.stock = { porValores };
    else if (general !== null) cambio.stock = { general: Math.max(0, Math.floor(general)) };
  }
  if (cambia(/^visibilidad$/) && p.visibilidad) cambio.estado = p.visibilidad === "visible" ? "active" : "paused";
  return { cambio: Object.keys(cambio).length > 0 ? cambio : null, noSeAplican: [...noSeAplican] };
}
